import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import RateApprovalModal from './RateApprovalModal';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { validatePhoneNumber } from '../utils/smsService';

const RateAdjustmentManager = ({ currentEmployeeNumber, isAdmin = true }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('submitted_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Comments state
  const [comments, setComments] = useState({});
  const [showComments, setShowComments] = useState({});
  const [newComment, setNewComment] = useState('');
  const [commentingRequestId, setCommentingRequestId] = useState(null);
  
  // Edit/Delete state
  const [editingRequest, setEditingRequest] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  // Whitelist state
  const [showWhitelist, setShowWhitelist] = useState(false);
  const [whitelistEntries, setWhitelistEntries] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [whitelistLoading, setWhitelistLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [maxRequests, setMaxRequests] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchRequests();
    
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchRequests, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Fetch comments for all requests
    if (requests.length > 0) {
      fetchCommentsForRequests(requests.map(r => r.id));
    }
  }, [requests]);

  useEffect(() => {
    if (showWhitelist) {
      fetchWhitelistData();
    }
  }, [showWhitelist]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('pay_rate_adjustment_requests')
        .select('*')
        .order('submitted_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching requests:', error);
        return;
      }
      
      setRequests(data || []);
    } catch (error) {
      console.error('Error in fetchRequests:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommentsForRequests = async (requestIds) => {
    try {
      const { data, error } = await supabase
        .from('rate_request_comments')
        .select('*')
        .in('request_id', requestIds)
        .order('created_at', { ascending: true });
      
      if (error) {
        console.error('Error fetching comments:', error);
        return;
      }
      
      // Group comments by request_id
      const commentsByRequest = {};
      (data || []).forEach(comment => {
        if (!commentsByRequest[comment.request_id]) {
          commentsByRequest[comment.request_id] = [];
        }
        commentsByRequest[comment.request_id].push(comment);
      });
      
      setComments(commentsByRequest);
    } catch (error) {
      console.error('Error in fetchCommentsForRequests:', error);
    }
  };

  const handleAddComment = async (requestId) => {
    if (!newComment.trim()) {
      alert('Please enter a comment');
      return;
    }

    try {
      const { error } = await supabase
        .from('rate_request_comments')
        .insert([{
          request_id: requestId,
          employee_number: currentEmployeeNumber || 'admin',
          comment_text: newComment.trim(),
          is_admin: isAdmin
        }]);

      if (error) {
        console.error('Error adding comment:', error);
        alert('Error adding comment: ' + error.message);
        return;
      }

      setNewComment('');
      setCommentingRequestId(null);
      fetchCommentsForRequests([requestId]);
    } catch (error) {
      console.error('Error in handleAddComment:', error);
      alert('Error adding comment. Please try again.');
    }
  };

  const handleDeleteRequest = async (requestId) => {
    try {
      const { error } = await supabase
        .from('pay_rate_adjustment_requests')
        .delete()
        .eq('id', requestId);

      if (error) {
        console.error('Error deleting request:', error);
        alert('Error deleting request: ' + error.message);
        return;
      }

      setShowDeleteConfirm(null);
      fetchRequests();
    } catch (error) {
      console.error('Error in handleDeleteRequest:', error);
      alert('Error deleting request. Please try again.');
    }
  };

  const canEditOrDelete = (request) => {
    if (isAdmin) return true;
    return request.employee_number === currentEmployeeNumber && 
           ['submitted', 'under_review'].includes(request.status);
  };

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setShowApprovalModal(true);
  };

  const handleApprovalComplete = () => {
    fetchRequests();
    setShowApprovalModal(false);
    setSelectedRequest(null);
  };

  const handleSendSMSNotification = async (request) => {
    try {
      // Fetch employee phone number
      const { data: employeeData, error: empError } = await supabase
        .from('employees')
        .select('phone, payroll_name')
        .eq('employee_number', request.employee_number)
        .single();

      if (empError || !employeeData) {
        alert('Error: Could not find employee information.');
        return;
      }

      if (!employeeData.phone || !validatePhoneNumber(employeeData.phone)) {
        alert('Error: Employee does not have a valid phone number.');
        return;
      }

      // Create personalized message
      const firstName = employeeData.payroll_name ? employeeData.payroll_name.split(' ')[0] : 'Hi';
      const message = `Hi ${firstName}! Your pay rate adjustment request #${request.request_no} has been received and is pending review. We will notify you once a decision is made. Check https://pay.starside.app for updates. Thank you!`;

      // Confirm before sending
      if (!window.confirm(`Send SMS notification to ${employeeData.payroll_name || request.employee_number}?\n\nMessage: ${message}`)) {
        return;
      }

      // Send SMS
      const recipients = [{
        employee_name: employeeData.payroll_name || request.employee_number,
        employee_number: request.employee_number,
        phone: employeeData.phone,
        message: message
      }];

      // Send SMS directly to API with skipPayrollValidation flag
      const smsResponse = await fetch('https://paystub.roborx.workers.dev/api/send-sms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipients: recipients,
          sendAll: false,
          skipPayrollValidation: true // Skip validation for rate adjustment notifications
        })
      });

      if (!smsResponse.ok) {
        const errorText = await smsResponse.text();
        throw new Error(`SMS API error: ${smsResponse.status} - ${errorText}`);
      }

      const result = await smsResponse.json();

      if (result.success) {
        alert(`✅ SMS notification sent successfully to ${employeeData.payroll_name || request.employee_number}!`);
      } else {
        alert(`⚠️ Failed to send SMS: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending SMS notification:', error);
      alert(`Error sending SMS notification: ${error.message}`);
    }
  };

  // Whitelist functions
  const fetchWhitelistData = async () => {
    try {
      setWhitelistLoading(true);
      
      // Fetch whitelist entries
      const { data: whitelist, error: whitelistError } = await supabase
        .from('pay_rate_adjustment_whitelist')
        .select('*')
        .order('added_at', { ascending: false });
      
      if (whitelistError) {
        console.error('Error fetching whitelist:', whitelistError);
      } else {
        setWhitelistEntries(whitelist || []);
      }

      // Fetch all employees
      const { data: employees, error: employeesError } = await supabase
        .from('employees')
        .select('employee_number, payroll_name')
        .order('payroll_name');
      
      if (employeesError) {
        console.error('Error fetching employees:', employeesError);
      } else {
        setAllEmployees(employees || []);
      }
    } catch (error) {
      console.error('Error in fetchWhitelistData:', error);
    } finally {
      setWhitelistLoading(false);
    }
  };

  const handleAddEmployee = async () => {
    if (!selectedEmployee) {
      alert('Please select an employee');
      return;
    }

    try {
      setIsSubmitting(true);

      const insertData = {
        employee_number: selectedEmployee,
        enabled: true,
        max_requests_per_year: maxRequests ? parseInt(maxRequests) : null,
        requests_this_year: 0,
        added_by: 'admin',
        notes: notes.trim() || null
      };

      const { error } = await supabase
        .from('pay_rate_adjustment_whitelist')
        .insert([insertData]);

      if (error) {
        if (error.code === '23505') {
          alert('This employee is already in the whitelist');
        } else {
          console.error('Error adding to whitelist:', error);
          alert('Error adding employee: ' + error.message);
        }
        return;
      }

      alert('✅ Employee added to whitelist successfully!');
      setShowAddModal(false);
      setSelectedEmployee('');
      setMaxRequests('');
      setNotes('');
      fetchWhitelistData();
    } catch (error) {
      console.error('Error in handleAddEmployee:', error);
      alert('Error adding employee. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleEnabled = async (entry) => {
    try {
      const { error } = await supabase
        .from('pay_rate_adjustment_whitelist')
        .update({ enabled: !entry.enabled, updated_at: new Date().toISOString() })
        .eq('id', entry.id);

      if (error) {
        console.error('Error updating whitelist:', error);
        alert('Error updating status: ' + error.message);
        return;
      }

      fetchWhitelistData();
    } catch (error) {
      console.error('Error in toggleEnabled:', error);
    }
  };

  const removeEmployee = async (entry) => {
    if (!window.confirm(`Are you sure you want to remove ${entry.employee_number} from the whitelist?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('pay_rate_adjustment_whitelist')
        .delete()
        .eq('id', entry.id);

      if (error) {
        console.error('Error removing from whitelist:', error);
        alert('Error removing employee: ' + error.message);
        return;
      }

      fetchWhitelistData();
    } catch (error) {
      console.error('Error in removeEmployee:', error);
    }
  };

  const resetYearlyCounter = async (entry) => {
    if (!window.confirm(`Reset request counter for ${entry.employee_number}?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('pay_rate_adjustment_whitelist')
        .update({ 
          requests_this_year: 0,
          updated_at: new Date().toISOString()
        })
        .eq('id', entry.id);

      if (error) {
        console.error('Error resetting counter:', error);
        alert('Error resetting counter: ' + error.message);
        return;
      }

      fetchWhitelistData();
    } catch (error) {
      console.error('Error in resetYearlyCounter:', error);
    }
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setMaxRequests(entry.max_requests_per_year ? entry.max_requests_per_year.toString() : '');
    setNotes(entry.notes || '');
    setShowEditModal(true);
  };

  const handleUpdateEntry = async () => {
    if (!editingEntry) return;

    try {
      setIsSubmitting(true);

      const updateData = {
        max_requests_per_year: maxRequests ? parseInt(maxRequests) : null,
        notes: notes.trim() || null,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('pay_rate_adjustment_whitelist')
        .update(updateData)
        .eq('id', editingEntry.id);

      if (error) {
        console.error('Error updating whitelist entry:', error);
        alert('Error updating entry: ' + error.message);
        return;
      }

      alert('✅ Whitelist entry updated successfully!');
      setShowEditModal(false);
      setEditingEntry(null);
      setMaxRequests('');
      setNotes('');
      fetchWhitelistData();
    } catch (error) {
      console.error('Error in handleUpdateEntry:', error);
      alert('Error updating entry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getEmployeeName = (employeeNumber) => {
    const employee = allEmployees.find(e => e.employee_number === employeeNumber);
    return employee ? employee.payroll_name : employeeNumber;
  };

  const availableEmployees = allEmployees.filter(
    emp => !whitelistEntries.some(wl => wl.employee_number === emp.employee_number)
  );

  const downloadRequestPDF = async (request) => {
    // Build compact rates HTML - single row layout
    let ratesHTML = '<div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-bottom: 10px;">';
    
    if (request.current_check_rate > 0 || request.requested_check_rate > 0) {
      ratesHTML += `
        <div style="background: #EFF6FF; padding: 8px; border-left: 3px solid #2563EB;">
          <div style="font-weight: bold; font-size: 10px; margin-bottom: 4px;">Check Rate / 支票工资</div>
          <div style="font-size: 10px; margin-bottom: 2px;">
            <span>Current:</span> <strong>$${parseFloat(request.current_check_rate || 0).toFixed(2)}</strong>
          </div>
          <div style="font-size: 10px; margin-bottom: 2px;">
            <span>Requested:</span> <strong style="color: #2563EB;">$${parseFloat(request.requested_check_rate || 0).toFixed(2)}</strong>
          </div>
          ${request.check_rate_increase > 0 ? `
            <div style="font-size: 9px; color: #16A34A; font-weight: bold;">
              +$${parseFloat(request.check_rate_increase).toFixed(2)} (+${request.check_rate_increase_pct?.toFixed(1)}%)
            </div>
          ` : ''}
        </div>
      `;
    }
    
    if (request.current_weekday_cash_rate > 0 || request.requested_weekday_cash_rate > 0) {
      ratesHTML += `
        <div style="background: #F0FDF4; padding: 8px; border-left: 3px solid #16A34A;">
          <div style="font-weight: bold; font-size: 10px; margin-bottom: 4px;">Weekday Cash / 工作日现金</div>
          <div style="font-size: 10px; margin-bottom: 2px;">
            <span>Current:</span> <strong>$${parseFloat(request.current_weekday_cash_rate || 0).toFixed(2)}</strong>
          </div>
          <div style="font-size: 10px; margin-bottom: 2px;">
            <span>Requested:</span> <strong style="color: #16A34A;">$${parseFloat(request.requested_weekday_cash_rate || 0).toFixed(2)}</strong>
          </div>
          ${request.weekday_cash_rate_increase > 0 ? `
            <div style="font-size: 9px; color: #16A34A; font-weight: bold;">
              +$${parseFloat(request.weekday_cash_rate_increase).toFixed(2)} (+${request.weekday_cash_increase_pct?.toFixed(1)}%)
            </div>
          ` : ''}
        </div>
      `;
    }
    
    if (request.current_weekend_cash_rate > 0 || request.requested_weekend_cash_rate > 0) {
      ratesHTML += `
        <div style="background: #FAF5FF; padding: 8px; border-left: 3px solid #9333EA;">
          <div style="font-weight: bold; font-size: 10px; margin-bottom: 4px;">Weekend Cash / 周末现金</div>
          <div style="font-size: 10px; margin-bottom: 2px;">
            <span>Current:</span> <strong>$${parseFloat(request.current_weekend_cash_rate || 0).toFixed(2)}</strong>
          </div>
          <div style="font-size: 10px; margin-bottom: 2px;">
            <span>Requested:</span> <strong style="color: #9333EA;">$${parseFloat(request.requested_weekend_cash_rate || 0).toFixed(2)}</strong>
          </div>
          ${request.weekend_cash_rate_increase > 0 ? `
            <div style="font-size: 9px; color: #16A34A; font-weight: bold;">
              +$${parseFloat(request.weekend_cash_rate_increase).toFixed(2)} (+${request.weekend_cash_increase_pct?.toFixed(1)}%)
            </div>
          ` : ''}
        </div>
      `;
    }
    
    ratesHTML += '</div>';
    
    // Create compact HTML content with proper Chinese font support
    const htmlContent = `
      <div id="pdf-content" style="width: 210mm; padding: 10mm; font-family: 'Microsoft YaHei', 'SimHei', 'STHeiti', Arial, sans-serif; background: white; color: black;">
        <!-- Compact Header -->
        <div style="text-align: center; margin-bottom: 12px; border-bottom: 2px solid #333; padding-bottom: 8px;">
          <h1 style="margin: 0; font-size: 22px; font-weight: bold;">PAY RATE ADJUSTMENT REQUEST</h1>
          <h2 style="margin: 2px 0 0 0; font-size: 18px; font-weight: bold;">员工加薪申请表</h2>
        </div>
        
        <!-- Compact Request Info -->
        <div style="margin-bottom: 10px; font-size: 11px; display: flex; justify-content: space-between;">
          <div><strong>Request # / 申请编号:</strong> ${request.request_no}</div>
          <div><strong>Status / 状态:</strong> ${request.status.toUpperCase()}</div>
          <div><strong>Date / 日期:</strong> ${formatDate(request.submitted_at)}</div>
        </div>
        
        <hr style="border: 1px solid #ccc; margin: 10px 0;" />
        
        <!-- Compact Employee Information -->
        <div style="margin-bottom: 10px;">
          <h3 style="margin: 0 0 6px 0; font-size: 13px; font-weight: bold;">EMPLOYEE INFORMATION / 员工信息</h3>
          <div style="font-size: 10px; line-height: 1.5; display: grid; grid-template-columns: repeat(2, 1fr); gap: 4px;">
            <div><strong>Employee Number / 员工编号:</strong> ${request.employee_number}</div>
            <div><strong>Employee Name / 姓名:</strong> ${request.employee_name || request.employee_number}</div>
            <div><strong>Company / 公司:</strong> ${request.company}</div>
            ${request.department ? `<div><strong>Department / 部门:</strong> ${request.department}</div>` : ''}
          </div>
        </div>
        
        <hr style="border: 1px solid #ccc; margin: 10px 0;" />
        
        <!-- Compact Rate Changes -->
        <div style="margin-bottom: 10px;">
          <h3 style="margin: 0 0 6px 0; font-size: 13px; font-weight: bold;">REQUESTED RATE CHANGES / 申请薪资变更</h3>
          ${ratesHTML}
        </div>
        
        <hr style="border: 1px solid #ccc; margin: 10px 0;" />
        
        <!-- Compact Request Details -->
        <div style="margin-bottom: 10px;">
          <h3 style="margin: 0 0 6px 0; font-size: 13px; font-weight: bold;">REQUEST DETAILS / 申请详情</h3>
          <div style="font-size: 10px; line-height: 1.4; margin-bottom: 8px;">
            <div style="margin-bottom: 4px;"><strong>Reason / 原因:</strong> ${request.reason || 'N/A'}</div>
            <div><strong>Requested Effective Date / 申请生效日期:</strong> ${formatDate(request.requested_effective_date)}</div>
          </div>
          
          <div style="margin-top: 8px;">
            <div style="font-weight: bold; font-size: 10px; margin-bottom: 4px;">Justification / 理由说明:</div>
            <div style="background: #F9FAFB; padding: 8px; border-radius: 4px; font-size: 9px; line-height: 1.4; white-space: pre-wrap; min-height: 40px;">
${request.justification || 'N/A'}
            </div>
          </div>
          
          ${request.achievements ? `
            <div style="margin-top: 8px;">
              <div style="font-weight: bold; font-size: 10px; margin-bottom: 4px;">Achievements / 工作成就:</div>
              <div style="background: #F9FAFB; padding: 8px; border-radius: 4px; font-size: 9px; line-height: 1.4; white-space: pre-wrap; min-height: 30px;">
${request.achievements}
              </div>
            </div>
          ` : ''}
        </div>
        
        ${(request.status === 'approved' || request.status === 'rejected') ? `
          <hr style="border: 1px solid #ccc; margin: 10px 0;" />
          
          <!-- Compact Approval Section -->
          <div style="margin-bottom: 10px;">
            <h3 style="margin: 0 0 6px 0; font-size: 13px; font-weight: bold; color: ${request.status === 'approved' ? '#16A34A' : '#DC2626'};">
              ${request.status === 'approved' ? 'APPROVED / 已批准' : 'REJECTED / 已拒绝'}
            </h3>
            <div style="font-size: 10px; line-height: 1.4;">
              ${request.approved_by ? `<div style="margin-bottom: 3px;"><strong>Approved By / 批准人:</strong> ${request.approved_by}</div>` : ''}
              ${request.approved_at ? `<div style="margin-bottom: 3px;"><strong>Approval Date / 批准日期:</strong> ${formatDate(request.approved_at)}</div>` : ''}
              ${request.final_effective_date ? `<div style="margin-bottom: 3px;"><strong>Effective Date / 生效日期:</strong> ${formatDate(request.final_effective_date)}</div>` : ''}
              ${request.final_approval_notes ? `
                <div style="margin-top: 6px;">
                  <strong>Approval Notes / 批准备注:</strong>
                  <div style="background: #F0FDF4; padding: 6px; border-radius: 4px; margin-top: 3px; font-size: 9px;">${request.final_approval_notes}</div>
                </div>
              ` : ''}
              ${request.rejection_reason ? `
                <div style="margin-top: 6px;">
                  <strong>Rejection Reason / 拒绝原因:</strong>
                  <div style="background: #FEF2F2; padding: 6px; border-radius: 4px; margin-top: 3px; font-size: 9px;">${request.rejection_reason}</div>
                </div>
              ` : ''}
            </div>
          </div>
        ` : `
          <hr style="border: 1px solid #ccc; margin: 10px 0;" />
          
          <!-- Compact Signature Section -->
          <div style="margin-top: 20px;">
            <h3 style="margin: 0 0 12px 0; font-size: 13px; font-weight: bold;">APPROVAL SIGNATURES / 审批签名</h3>
            <div style="display: flex; justify-content: space-between; margin-top: 15px;">
              <div style="width: 45%;">
                <div style="font-size: 10px; font-weight: bold; margin-bottom: 15px;">Manager Approval / 经理批准:</div>
                <div style="border-bottom: 2px solid #000; margin-bottom: 6px;"></div>
                <div style="font-size: 9px;">Date / 日期: ______________</div>
              </div>
              <div style="width: 45%;">
                <div style="font-size: 10px; font-weight: bold; margin-bottom: 15px;">HR Approval / 人事批准:</div>
                <div style="border-bottom: 2px solid #000; margin-bottom: 6px;"></div>
                <div style="font-size: 9px;">Date / 日期: ______________</div>
              </div>
            </div>
          </div>
        `}
      </div>
    `;
    
    // Create temporary container
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.width = '210mm';
    container.innerHTML = htmlContent;
    document.body.appendChild(container);
    
    try {
      // Convert HTML to canvas with higher scale for better quality
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: container.scrollWidth,
        height: container.scrollHeight
      });
      
      // Create PDF from canvas with proper multi-page handling
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Calculate how many pages we need
      const totalPages = Math.ceil(imgHeight / pageHeight);
      
      // Add image across multiple pages
      for (let i = 0; i < totalPages; i++) {
        if (i > 0) {
          pdf.addPage();
        }
        
        // Calculate the y position for this page
        const yPosition = -(i * pageHeight);
        
        // Add the image, offsetting it so the correct portion shows on each page
        pdf.addImage(imgData, 'PNG', 0, yPosition, imgWidth, imgHeight);
      }
      
      // Save PDF
      pdf.save(`Rate_Request_${request.request_no}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      // Clean up
      document.body.removeChild(container);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'submitted': { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', icon: '⏳', label: 'Pending' },
      'under_review': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: '🔍', label: 'Under Review' },
      'pending_approval': { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', icon: '⏰', label: 'Pending Approval' },
      'approved': { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', icon: '✅', label: 'Approved' },
      'rejected': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: '❌', label: 'Rejected' },
      'implemented': { bg: 'bg-slate-900', text: 'text-slate-50', border: 'border-slate-800', icon: '💰', label: 'Implemented' }
    };
    
    const config = statusConfig[status] || statusConfig['submitted'];
    
    return (
      <span className={`${config.bg} ${config.text} ${config.border} border px-2.5 py-0.5 rounded-md text-xs font-medium inline-flex items-center gap-1.5`}>
        <span>{config.icon}</span>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };

  // Filter and sort requests
  const filteredRequests = requests
    .filter(r => {
      // Status filter
      if (filterStatus === 'pending') {
        return ['submitted', 'under_review', 'pending_approval'].includes(r.status);
      } else if (filterStatus !== 'all') {
        return r.status === filterStatus;
      }
      return true;
    })
    .filter(r => {
      // Search filter
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        r.employee_name?.toLowerCase().includes(term) ||
        r.employee_number?.toLowerCase().includes(term) ||
        r.request_no?.toLowerCase().includes(term) ||
        r.company?.toLowerCase().includes(term)
      );
    })
    .sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'submitted_at':
          comparison = new Date(a.submitted_at) - new Date(b.submitted_at);
          break;
        case 'employee_name':
          comparison = (a.employee_name || a.employee_number || '').localeCompare(b.employee_name || b.employee_number || '');
          break;
        case 'increase_pct':
          comparison = (a.check_rate_increase_pct || 0) - (b.check_rate_increase_pct || 0);
          break;
        default:
          comparison = 0;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  // Calculate statistics
  const stats = {
    total: requests.length,
    pending: requests.filter(r => ['submitted', 'under_review', 'pending_approval'].includes(r.status)).length,
    approved: requests.filter(r => r.status === 'approved' || r.status === 'implemented').length,
    rejected: requests.filter(r => r.status === 'rejected').length,
    avgIncrease: requests.length > 0 
      ? requests.reduce((sum, r) => sum + (r.check_rate_increase_pct || 0), 0) / requests.length 
      : 0
  };

  if (loading) {
    return (
      <div className="rounded-lg border bg-white p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded-md w-1/3"></div>
          <div className="grid grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-24 bg-gray-200 rounded-md"></div>
            ))}
          </div>
          <div className="h-64 bg-gray-200 rounded-md"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-lg border bg-white p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">Pay Rate Adjustment Requests</h2>
            <p className="text-sm text-gray-600 mt-1">Review and manage employee rate increase requests</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowWhitelist(!showWhitelist)}
              className={`inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none h-10 px-4 py-2 ${
                showWhitelist 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
              }`}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {showWhitelist ? 'Hide Whitelist' : 'Manage Whitelist'}
            </button>
            <button
              onClick={fetchRequests}
              className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none bg-blue-600 text-white hover:bg-blue-700 h-10 px-4 py-2"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="rounded-lg border bg-white p-4">
            <p className="text-xs font-medium text-gray-600 mb-1">Total Requests</p>
            <p className="text-3xl font-bold">{stats.total}</p>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <p className="text-xs font-medium text-gray-600 mb-1">Pending Review</p>
            <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <p className="text-xs font-medium text-gray-600 mb-1">Approved</p>
            <p className="text-3xl font-bold text-green-600">{stats.approved}</p>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <p className="text-xs font-medium text-gray-600 mb-1">Rejected</p>
            <p className="text-3xl font-bold text-red-600">{stats.rejected}</p>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <p className="text-xs font-medium text-gray-600 mb-1">Avg Increase</p>
            <p className="text-3xl font-bold text-purple-600">{stats.avgIncrease.toFixed(1)}%</p>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="rounded-lg border bg-white p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by name, employee #, or request #..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending Review</option>
            <option value="submitted">Submitted</option>
            <option value="under_review">Under Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="implemented">Implemented</option>
          </select>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="submitted_at">Date Submitted</option>
            <option value="employee_name">Employee Name</option>
            <option value="increase_pct">% Increase</option>
          </select>

          {/* Sort Order */}
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none border border-gray-300 bg-white hover:bg-gray-100 h-10 px-4 py-2"
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      {/* Requests List */}
      <div className="rounded-lg border bg-white p-6">
        {filteredRequests.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📋</div>
            <p className="text-gray-600 text-lg">No rate adjustment requests found</p>
            {(searchTerm || filterStatus !== 'all') && (
              <button
                onClick={() => { setSearchTerm(''); setFilterStatus('all'); }}
                className="mt-4 inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none bg-blue-600 text-white hover:bg-blue-700 h-9 px-4 py-2"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredRequests.map((request) => (
              <div
                key={request.id}
                className="rounded-lg border bg-white p-4 hover:bg-gray-50 transition-colors"
              >
                {/* Compact Header Row */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <h3 className="font-semibold text-base">{request.request_no}</h3>
                    {getStatusBadge(request.status)}
                    <span className="text-sm text-gray-500">•</span>
                    <span className="text-sm font-medium truncate">{request.employee_name || request.employee_number}</span>
                    <span className="text-xs text-gray-500">#{request.employee_number}</span>
                    <span className="text-xs text-gray-500">• {request.company}</span>
                    {request.department && <span className="text-xs text-gray-500">• {request.department}</span>}
                  </div>
                  <div className="text-xs text-gray-500 whitespace-nowrap ml-2">
                    {formatDate(request.submitted_at)}
                  </div>
                </div>

                {/* Compact Rate Cards - Single Row */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {(request.current_check_rate > 0 || request.requested_check_rate > 0) && (
                    <div className="rounded-md border bg-blue-50/50 p-2 border-l-4 border-blue-500">
                      <p className="text-xs font-medium text-gray-600 mb-0.5">Check</p>
                      <div className="flex items-center gap-1 text-xs">
                        <span className="font-medium">{formatCurrency(request.current_check_rate)}</span>
                        <span className="text-gray-400">→</span>
                        <span className="font-semibold text-blue-700">{formatCurrency(request.requested_check_rate)}</span>
                      </div>
                      {request.check_rate_increase > 0 && (
                        <p className="text-xs text-green-600 font-semibold mt-0.5">
                          +{formatCurrency(request.check_rate_increase)} ({request.check_rate_increase_pct?.toFixed(1)}%)
                        </p>
                      )}
                    </div>
                  )}
                  {(request.current_weekday_cash_rate > 0 || request.requested_weekday_cash_rate > 0) && (
                    <div className="rounded-md border bg-green-50/50 p-2 border-l-4 border-green-500">
                      <p className="text-xs font-medium text-muted-foreground mb-0.5">Weekday</p>
                      <div className="flex items-center gap-1 text-xs">
                        <span className="font-medium">{formatCurrency(request.current_weekday_cash_rate)}</span>
                        <span className="text-gray-400">→</span>
                        <span className="font-semibold text-green-700">{formatCurrency(request.requested_weekday_cash_rate)}</span>
                      </div>
                      {request.weekday_cash_rate_increase > 0 && (
                        <p className="text-xs text-green-600 font-semibold mt-0.5">
                          +{formatCurrency(request.weekday_cash_rate_increase)} ({request.weekday_cash_increase_pct?.toFixed(1)}%)
                        </p>
                      )}
                    </div>
                  )}
                  {(request.current_weekend_cash_rate > 0 || request.requested_weekend_cash_rate > 0) && (
                    <div className="rounded-md border bg-purple-50/50 p-2 border-l-4 border-purple-500">
                      <p className="text-xs font-medium text-muted-foreground mb-0.5">Weekend</p>
                      <div className="flex items-center gap-1 text-xs">
                        <span className="font-medium">{formatCurrency(request.current_weekend_cash_rate)}</span>
                        <span className="text-gray-400">→</span>
                        <span className="font-semibold text-purple-700">{formatCurrency(request.requested_weekend_cash_rate)}</span>
                      </div>
                      {request.weekend_cash_rate_increase > 0 && (
                        <p className="text-xs text-green-600 font-semibold mt-0.5">
                          +{formatCurrency(request.weekend_cash_rate_increase)} ({request.weekend_cash_increase_pct?.toFixed(1)}%)
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Compact Details - Collapsible */}
                <div className="mb-3">
                  <div className="flex items-center gap-2 text-xs text-gray-600 mb-1">
                    <span><strong>Reason:</strong> {request.reason}</span>
                  </div>
                  <details className="text-xs">
                    <summary className="cursor-pointer text-gray-600 hover:text-gray-900 font-medium mb-1">
                      View Details
                    </summary>
                    <div className="mt-2 space-y-2 pl-2 border-l-2 border-gray-200">
                      <div>
                        <p className="font-semibold mb-1">Justification:</p>
                        <p className="text-gray-600 bg-gray-50 p-2 rounded-md text-xs">{request.justification}</p>
                      </div>
                      {request.achievements && (
                        <div>
                          <p className="font-semibold mb-1">Achievements:</p>
                          <p className="text-gray-600 bg-gray-50 p-2 rounded-md text-xs whitespace-pre-wrap">{request.achievements}</p>
                        </div>
                      )}
                    </div>
                  </details>
                </div>

                {/* Approval/Rejection Notes */}
                {request.status === 'approved' && request.final_approval_notes && (
                  <div className="bg-green-50 border-l-4 border-green-500 rounded-md p-2 mb-3">
                    <p className="text-xs font-semibold text-green-800 mb-1">✅ Approval Notes:</p>
                    <p className="text-xs text-gray-700">{request.final_approval_notes}</p>
                  </div>
                )}
                {request.status === 'rejected' && request.rejection_reason && (
                  <div className="bg-red-50 border-l-4 border-red-500 rounded-md p-2 mb-3">
                    <p className="text-xs font-semibold text-red-800 mb-1">❌ Rejection Reason:</p>
                    <p className="text-xs text-gray-700">{request.rejection_reason}</p>
                  </div>
                )}

                {/* Comments Section */}
                {showComments[request.id] && (
                  <div className="mb-3 border-t pt-3">
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {(comments[request.id] || []).map((comment) => (
                        <div key={comment.id} className={`text-xs p-2 rounded-md border ${comment.is_admin ? 'bg-blue-50/50 border-l-4 border-blue-500' : 'bg-gray-50 border-l-4 border-gray-200'}`}>
                          <div className="flex justify-between items-start mb-1">
                            <span className="font-semibold text-gray-900">
                              {comment.is_admin ? '👤 Admin' : `👤 ${comment.employee_number}`}
                            </span>
                            <span className="text-gray-500">
                              {new Date(comment.created_at).toLocaleDateString()} {new Date(comment.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          <p className="text-gray-700">{comment.comment_text}</p>
                        </div>
                      ))}
                    </div>
                    {commentingRequestId === request.id ? (
                      <div className="mt-2">
                        <textarea
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          placeholder="Add a comment..."
                          rows="2"
                          className="flex min-h-[60px] w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-xs placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        />
                        <div className="flex gap-2 mt-1">
                          <button
                            onClick={() => handleAddComment(request.id)}
                            className="inline-flex items-center justify-center rounded-md text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none bg-blue-600 text-white hover:bg-blue-700 h-8 px-3 py-1"
                          >
                            Post
                          </button>
                          <button
                            onClick={() => {
                              setCommentingRequestId(null);
                              setNewComment('');
                            }}
                            className="inline-flex items-center justify-center rounded-md text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none border border-gray-300 bg-white hover:bg-gray-100 h-8 px-3 py-1"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setCommentingRequestId(request.id)}
                        className="mt-2 text-xs text-blue-600 hover:text-blue-700 font-medium"
                      >
                        + Add Comment
                      </button>
                    )}
                  </div>
                )}

                {/* Action Buttons - Compact Row */}
                <div className="flex flex-wrap gap-2 pt-3 border-t">
                  
                  <button
                    onClick={() => setShowComments({ ...showComments, [request.id]: !showComments[request.id] })}
                    className="inline-flex items-center justify-center rounded-md text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none border border-gray-300 bg-white hover:bg-gray-100 text-gray-700 h-8 px-3 py-1"
                  >
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    Comments ({(comments[request.id] || []).length})
                  </button>

                  {canEditOrDelete(request) && (
                    <>
                      {['submitted', 'under_review'].includes(request.status) && (
                        <button
                          onClick={() => setEditingRequest(request)}
                          className="inline-flex items-center justify-center rounded-md text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background border border-input bg-background hover:bg-accent hover:text-accent-foreground h-8 px-3 py-1"
                        >
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Edit
                        </button>
                      )}
                      <button
                        onClick={() => setShowDeleteConfirm(request.id)}
                        className="inline-flex items-center justify-center rounded-md text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none bg-red-600 text-white hover:bg-red-700 h-8 px-3 py-1"
                      >
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete
                      </button>
                    </>
                  )}
                  
                  {['submitted', 'under_review', 'pending_approval'].includes(request.status) && isAdmin && (
                    <>
                      <button
                        onClick={() => handleSendSMSNotification(request)}
                        className="inline-flex items-center justify-center rounded-md text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 h-8 px-3 py-1"
                        title="Send SMS notification to employee"
                      >
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        SMS
                      </button>
                      <button
                        onClick={() => handleApprove(request)}
                        className="inline-flex items-center justify-center rounded-md text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background bg-green-600 text-white hover:bg-green-700 h-8 px-3 py-1 ml-auto"
                      >
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Review
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Whitelist Section */}
      {showWhitelist && (
        <div className="rounded-lg border bg-white p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold tracking-tight">Rate Request Whitelist</h3>
              <p className="text-sm text-gray-600 mt-1">
                {whitelistEntries.filter(e => e.enabled).length} of {whitelistEntries.length} employees can submit rate requests
              </p>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none bg-green-600 text-white hover:bg-green-700 h-10 px-4 py-2"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Employee
            </button>
          </div>

          {/* Info Card */}
          <div className="rounded-lg border bg-blue-50 border-blue-200 p-3">
            <p className="text-sm text-blue-800">
              <strong>ℹ️ About Whitelist:</strong> Only employees on this whitelist can submit pay rate adjustment requests. 
              You can set limits on how many requests each employee can make per year.
            </p>
          </div>

          {/* Whitelist Table */}
          {whitelistLoading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-32 bg-gray-200 rounded"></div>
            </div>
          ) : whitelistEntries.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-base mb-4">No employees in whitelist yet</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none bg-green-600 text-white hover:bg-green-700 h-10 px-4 py-2"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add First Employee
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Employee</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Max Requests/Year</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Requests This Year</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Last Request</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Notes</th>
                    <th className="px-1 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {whitelistEntries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{getEmployeeName(entry.employee_number)}</p>
                          <p className="text-xs text-gray-500">#{entry.employee_number}</p>
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {entry.enabled ? (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Enabled
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200">
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            Disabled
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-center">
                        <span className="text-sm font-medium text-gray-900">
                          {entry.max_requests_per_year || '∞'}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-center">
                        <span className={`text-sm font-medium ${
                          entry.max_requests_per_year && entry.requests_this_year >= entry.max_requests_per_year
                            ? 'text-red-600'
                            : 'text-gray-900'
                        }`}>
                          {entry.requests_this_year}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="text-sm text-gray-600">
                          {entry.last_request_date 
                            ? new Date(entry.last_request_date).toLocaleDateString()
                            : 'Never'}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-sm text-gray-600 truncate block max-w-xs">
                          {entry.notes || '—'}
                        </span>
                      </td>
                      <td className="px-1 py-2 whitespace-nowrap">
                        <div className="flex items-center justify-center -space-x-0.5">
                          <button
                            onClick={() => toggleEnabled(entry)}
                            className={`relative inline-flex items-center justify-center p-0.5 text-xs transition-colors rounded focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-offset-1 border-0 bg-transparent ${
                              entry.enabled
                                ? 'hover:bg-yellow-100 text-yellow-600 focus-visible:ring-yellow-500'
                                : 'hover:bg-green-100 text-green-600 focus-visible:ring-green-500'
                            } group`}
                            title={entry.enabled ? 'Disable' : 'Enable'}
                          >
                            {entry.enabled ? (
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            ) : (
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            )}
                            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-1.5 py-0.5 text-xs font-medium text-white bg-gray-900 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                              {entry.enabled ? 'Disable' : 'Enable'}
                            </span>
                          </button>
                          
                          <button
                            onClick={() => handleEditEntry(entry)}
                            className="relative inline-flex items-center justify-center p-0.5 text-xs transition-colors rounded focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-offset-1 border-0 bg-transparent hover:bg-indigo-100 text-indigo-600 focus-visible:ring-indigo-500 group"
                            title="Edit"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-1.5 py-0.5 text-xs font-medium text-white bg-gray-900 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                              Edit
                            </span>
                          </button>
                          
                          {entry.requests_this_year > 0 && (
                            <button
                              onClick={() => resetYearlyCounter(entry)}
                              className="relative inline-flex items-center justify-center p-0.5 text-xs transition-colors rounded focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-offset-1 border-0 bg-transparent hover:bg-blue-100 text-blue-600 focus-visible:ring-blue-500 group"
                              title="Reset counter"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-1.5 py-0.5 text-xs font-medium text-white bg-gray-900 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                                Reset Counter
                              </span>
                            </button>
                          )}
                          
                          <button
                            onClick={() => removeEmployee(entry)}
                            className="relative inline-flex items-center justify-center p-0.5 text-xs transition-colors rounded focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-offset-1 border-0 bg-transparent hover:bg-red-100 text-red-600 focus-visible:ring-red-500 group"
                            title="Remove"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-1.5 py-0.5 text-xs font-medium text-white bg-gray-900 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                              Remove
                            </span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Add Employee Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Add Employee to Whitelist</h3>
            
            <div className="space-y-4">
              {/* Employee Select */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Select Employee *</label>
                <select
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                >
                  <option value="">Choose an employee...</option>
                  {availableEmployees.map((emp) => (
                    <option key={emp.employee_number} value={emp.employee_number}>
                      {emp.payroll_name} (#{emp.employee_number})
                    </option>
                  ))}
                </select>
                {availableEmployees.length === 0 && (
                  <p className="text-sm text-gray-500 mt-1">All employees are already in the whitelist</p>
                )}
              </div>

              {/* Max Requests */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Max Requests Per Year <span className="text-xs text-gray-500 font-normal">(leave empty for unlimited)</span>
                </label>
                <input
                  type="number"
                  value={maxRequests}
                  onChange={(e) => setMaxRequests(e.target.value)}
                  min="1"
                  placeholder="Unlimited"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Notes <span className="text-xs text-gray-500 font-normal">(optional)</span>
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows="2"
                  placeholder="Add any notes about why this employee is whitelisted..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedEmployee('');
                  setMaxRequests('');
                  setNotes('');
                }}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddEmployee}
                disabled={isSubmitting || !selectedEmployee}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Adding...' : 'Add Employee'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Whitelist Entry Modal */}
      {showEditModal && editingEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Edit Whitelist Entry</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingEntry(null);
                  setMaxRequests('');
                  setNotes('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm font-semibold text-gray-700">Employee</p>
              <p className="text-lg font-bold text-gray-800">{getEmployeeName(editingEntry.employee_number)}</p>
              <p className="text-xs text-gray-500">#{editingEntry.employee_number}</p>
            </div>
            
            <div className="space-y-4">
              {/* Max Requests */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Max Requests Per Year <span className="text-xs text-gray-500 font-normal">(leave empty for unlimited)</span>
                </label>
                <input
                  type="number"
                  value={maxRequests}
                  onChange={(e) => setMaxRequests(e.target.value)}
                  min="1"
                  placeholder="Unlimited"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Notes <span className="text-xs text-gray-500 font-normal">(optional)</span>
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows="3"
                  placeholder="Add any notes about this whitelist entry..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingEntry(null);
                  setMaxRequests('');
                  setNotes('');
                }}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateEntry}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Updating...' : 'Update Entry'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Delete Request</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this rate adjustment request? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteRequest(showDeleteConfirm)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal - Placeholder (can be enhanced later) */}
      {editingRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Edit Request {editingRequest.request_no}</h3>
            <p className="text-gray-600 mb-4">
              Edit functionality will be implemented. For now, please delete and recreate the request if needed.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setEditingRequest(null)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedRequest && (
        <RateApprovalModal
          isOpen={showApprovalModal}
          onClose={() => {
            setShowApprovalModal(false);
            setSelectedRequest(null);
          }}
          request={selectedRequest}
          onApprovalComplete={handleApprovalComplete}
        />
      )}
    </div>
  );
};

export default RateAdjustmentManager;

